import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, RotateCw, X } from "lucide-react";
import { useCheckInSend, useCheckInSelector } from "../context";
import { checkInSelectors } from "../selectors";
import { useAudio } from "../../../../../lib/use-audio";
import { docCirrusRpcClient } from "../../../../../lib/rpc/doc-cirrus.client";
import { frontdeskDiagnosticLogger } from "../../../../../lib/diagnostic-logger";
import { KioskStep } from "../../../../../components/kiosk-step";
import { KioskStatePanel } from "../../../../../components/kiosk-state-panel";
import {
  dateOfBirthToDate,
  formatDateOfBirthForDisplay,
} from "../../../components/DateOfBirthInput";
import { useCardAction } from "../../use-card-action";

const ISO_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;

/**
 * Format a diff value for the patient: dates in German format, booleans as
 * words, structured values as their readable parts — never raw JSON.
 */
function formatValue(
  value: unknown,
  words: { readonly yes: string; readonly no: string },
): string {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  if (typeof value === "boolean") {
    return value ? words.yes : words.no;
  }
  if (typeof value === "string") {
    return ISO_DATE_PREFIX.test(value) && dateOfBirthToDate(value) !== undefined
      ? formatDateOfBirthForDisplay(value)
      : value;
  }
  if (typeof value === "number" || typeof value === "bigint") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((entry) => formatValue(entry, words)).join(", ");
  }
  if (typeof value === "object") {
    return Object.values(value)
      .filter((entry) => entry !== null && entry !== undefined && entry !== "")
      .map((entry) => formatValue(entry, words))
      .join(" ");
  }
  return "—";
}

/**
 * Diff review step: shown when the inserted card is newer than the stored
 * record. Lists only the changed fields (before → new) and lets the patient
 * either take over the card data or check in with the record unchanged
 * (KC-18).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <DiffReviewStep />
 * ```
 */
export function DiffReviewStep() {
  const { t } = useTranslation();
  const send = useCheckInSend();
  const diffData = useCheckInSelector(checkInSelectors.diffData);
  const crlogId = useCheckInSelector(checkInSelectors.crlogId);
  const { run, isPending: isApplying } = useCardAction();
  const [error, setError] = useState<string>();
  const words = {
    yes: t("self-service.check-in.diff-review.value-yes"),
    no: t("self-service.check-in.diff-review.value-no"),
  };

  // Play audio on mount
  useAudio("checkIn-diffReview");

  const handleConfirm = async () => {
    // Apply the merged card data to the patient record before continuing
    if (crlogId) {
      setError(undefined);
      try {
        const result = await run(() => docCirrusRpcClient.frontdesk.applyCardData({ crlogId }));
        if (!result) return;
        if (result.crlogId !== crlogId || result.status !== "APPLIED" || result.validationStatus !== "OK") {
          throw new Error("Card changes were not confirmed as applied and valid");
        }
      } catch (err) {
        frontdeskDiagnosticLogger.error("Failed to apply card data", {
          error: err,
          hasCrlogId: Boolean(crlogId),
        });
        setError(t("self-service.check-in.diff-review.apply-error"));
        return;
      }
    } else {
      setError(t("self-service.check-in.diff-review.apply-error"));
      return;
    }
    send({ type: "CONFIRM_DIFFS" });
  };

  // Filter to only show changed fields
  const changedFields = diffData?.filter((field) => field.changed) ?? [];

  return (
    <KioskStep
      width="narrow"
      title={t("self-service.check-in.diff-review.title-v2")}
      lead={t("self-service.check-in.diff-review.subtitle-v2")}
      primaryAction={{
        label: error
          ? t("self-service.check-in.diff-review.retry")
          : t("self-service.check-in.diff-review.confirm-v2"),
        icon: error ? RotateCw : Check,
        onClick: () => void handleConfirm(),
        busy: isApplying,
        testId: "frontdesk-diff-review-confirm-btn",
      }}
      secondaryAction={{
        label: t("self-service.check-in.diff-review.keep"),
        icon: X,
        onClick: () => send({ type: "KEEP_RECORD" }),
        disabled: isApplying,
        testId: "frontdesk-diff-review-keep-btn",
      }}
    >
      {changedFields.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {changedFields.map((field, index) => (
            <li
              key={field.path ?? index}
              className="flex min-w-0 flex-col gap-3 rounded-3xl border bg-card p-6"
            >
              <span className="text-xl font-semibold text-foreground">
                {field.label ??
                  field.path ??
                  t("self-service.check-in.diff-review.field-fallback")}
              </span>
              <dl className="m-0 grid min-w-0 gap-3 sm:grid-cols-2">
                <div className="flex min-w-0 flex-col gap-1">
                  <dt className="text-base text-foreground">
                    {t("self-service.check-in.diff-review.col-current-v2")}
                  </dt>
                  <dd className="m-0 text-lg break-words text-foreground line-through decoration-foreground/60">
                    {formatValue(field.patientValue, words)}
                  </dd>
                </div>
                <div className="flex min-w-0 flex-col gap-1">
                  <dt className="text-base text-foreground">
                    {t("self-service.check-in.diff-review.col-new-v2")}
                  </dt>
                  <dd className="m-0 text-lg font-semibold break-words text-foreground">
                    {formatValue(field.cardValue, words)}
                  </dd>
                </div>
              </dl>
            </li>
          ))}
        </ul>
      ) : (
        <KioskStatePanel
          tone="neutral"
          title={t("self-service.check-in.diff-review.no-changes")}
        />
      )}

      <p className="m-0 text-lg leading-normal text-foreground">
        {t("self-service.check-in.diff-review.note")}
      </p>

      {error ? (
        <KioskStatePanel
          tone="error"
          title={error}
          testId="frontdesk-diff-review-error"
        />
      ) : null}
    </KioskStep>
  );
}
